





































import { Component, Prop, Vue, Watch } from "@/shared/vuepack";
import _ from "lodash";
import { HsabExpenseCategory, HsabExpenseSubTypeResponse, PayFromPlanOption } from "@/shared/types";
import moment, { Moment } from "moment";
import { formatCurrency } from "@/shared/currency.js";
import { useHsabExpenseStore } from "@/employee/store/hsabExpense";
import { useHsabAccountsStore } from "@/employee/store/hsabAccounts";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";
import { getNotionalAccountDisplay } from "@/employee/Utilities/HsabAccountUtility";
import { useHsabExpenseFormStore } from "@/employee/store/hsabExpenseForm";
import { isNullEmptyOrUndefined } from "@/shared/stringHelper";
import { FirstDayOfExistenceForHsaAccounts } from "@/employee/Utilities/HsabExpenseUtility";
import * as dateHelper from "@/shared/dateHelper";

@Component({})
export default class HsabExpensePayFromDisplay extends Vue {
  formatCurrency = formatCurrency;
  hsabExpenseStore = useHsabExpenseStore();
  hsabAccountsStore = useHsabAccountsStore();
  hsabExpenseFormStore = useHsabExpenseFormStore();
  featureFlagStore = useFeatureFlagStore();
  possiblePlans: PayFromPlanOption[] = [];
  possibleSubTypes: HsabExpenseSubTypeResponse[] = [];
  payFromHasHsaAndNotional = false;
  allowHsaPlans = false; // default value calculated in mounted()
  forceHsaPlans = false;
  disableAllowHsaPlansToggle = false;
  disableForceHsaPlansToggle = false;

  @Prop()
  currentDate: Moment;

  @Prop()
  currentCategory: HsabExpenseCategory;

  @Prop()
  currentType: string;

  @Prop() currentSubType: string;

  @Watch("plansToDisplay")
  onPayFromDisplayChanged (newVal: PayFromPlanOption[]) {
    let combinedPlanName: string;
    combinedPlanName = this.possiblePlans
        .map(plan => {
          if (!isNullEmptyOrUndefined(plan.planDisplayName)) {
            if (plan.account && plan.account.accountBalance !== null && plan.account.accountBalance !== undefined) {
              const balanceText = formatCurrency(plan.account.accountBalance);
              return plan.planDisplayName + " " + balanceText;
            } else {
              return plan.planDisplayName;
            }
          } else {
            return "";
          }
        })
        .filter(name => name !== "")
        .join(", ");

    this.hsabExpenseFormStore.payFromOption = combinedPlanName;
  }

  @Watch("currentCategory")
  onCategoryChanged () {
    this.calculateAndUpdatePayFrom();
  }

  @Watch("currentType")
  onTypeChanged () {
    this.calculateAndUpdatePayFrom();
  }

  @Watch("currentSubType")
  onSubTypeChanged () {
    this.calculateAndUpdatePayFrom();
  }

  @Watch("currentDate")
  onCurrentDateChanged () {
    this.calculateAndUpdatePayFrom();
  }

  @Watch("hsabExpenseStore.expenseTypesAndSubtypesByCategories")
  onUserCategoriesChanges () {
    this.calculateAndUpdatePayFrom();
  }

  @Watch("hsabExpenseFormStore.expenseForm.isRecurring")
  onIsRecurringChanged (newVal: boolean) {
    if (newVal) {
      this.setAllowHsaPlans(false);
    }
  }

  @Watch("hsabExpenseFormStore.isTravelCostReimbursementEnabled")
  onIsTravelReimbursedChanged (newVal: boolean) {
    if (newVal) {
      this.setAllowHsaPlans(false);
    }
  }

  created () {
    this.hsabAccountsStore.getHsabAccounts();
    this.hsabExpenseStore.loadExpenseCategories();
  }

  mounted () {
    this.setDefaultAllowHsa();
  }

  get requirementsLoaded () {
    return this.hsabAccountsStore.allHsabAccountsLoaded && this.hsabExpenseStore.isCategoryDataLoaded;
  }

  get showPayFrom () {
    const hasMultipleActivePlans = this.hsabAccountsStore.allActiveHsabAccounts.length > 1;
    return hasMultipleActivePlans
        || this.displayPayFromError;
  }

  getSinglePlanDisplay () {
    let result = "";
    if (this.plansToDisplay.length === 1) {
      result = this.getPlanDisplay(this.plansToDisplay[0]);
    }
    return result;
  }

  getPlanDisplay (item: PayFromPlanOption) {
    let result = item.planDisplayName;
    if (item.account) {
      result = getNotionalAccountDisplay(item.account, false);

      if (item.account.accountBalance !== null) {
        result += " (" + formatCurrency(item.account.accountBalance) + ")";
      }
    }
    return result;
  }

  getPlanStartDate (plan: PayFromPlanOption): moment.Moment {
    if (plan.isHsa) {
      return FirstDayOfExistenceForHsaAccounts;
    } else {
      return plan.planStart;
    }
  }

  get displayPayFromError () {
    return this.hsabExpenseFormStore.isPayFromRequiredToSubmit;
  }

  get plansToDisplay () {

    if (this.forceHsaPlans) {
      return this.possiblePlans.filter(x => x.isHsa);
    }

    if (this.possiblePlans.length > 1 && this.hasHsaAsOption) {
      this.payFromHasHsaAndNotional = true;
      return this.possiblePlans.filter(x => !x.isHsa);
    } else {
      this.payFromHasHsaAndNotional = false;
    }
    return this.possiblePlans;
  }

  get showAllowHsaPlansToggle () {
    return this.hasHsaAsOption
        && this.hasNotionalAsOption
        && this.hsabExpenseFormStore.expenseForm.isRecurring === false
        && this.hsabExpenseFormStore.isTravelCostReimbursementEnabled === false;
  }

  get showForceHsaToggle () {
    return this.featureFlagStore.flags.bend27800AllowForcingHSAPlansForExpenses
        && this.hsabAccountsStore.userHasActiveHsaAccounts
        && this.hasHsaAsOption
        && this.hasNotionalAsOption;
  }

  get hasHsaAsOption () {
    return _.some(this.possiblePlans, x => x.isHsa);
  }

  get hasNotionalAsOption () {
    return _.some(this.possiblePlans, x => !x.isHsa);
  }

  calculateAndUpdatePayFrom () {
    const previousPossiblePlans = _.clone(this.possiblePlans);
    this.possiblePlans = [];
    this.possibleSubTypes = [];

    const hasCategory = this.currentCategory !== undefined && this.currentCategory !== null && this.currentCategory !== HsabExpenseCategory.Unknown;
    const hasType = this.currentType !== undefined && this.currentType !== null && this.currentType !== "";
    const hasSubType = this.currentSubType !== undefined && this.currentSubType !== null && this.currentSubType !== "";

    if (hasCategory) {
      const types = this.hsabExpenseStore.getExpenseTypesAndSubtypesByCategory(this.currentCategory);

      if (hasType) {
        const type = types.find(x => x.expenseType === this.currentType);
        if (type) {
          if (hasSubType) {
            const subType = type.expenseSubTypes.find(x => x.subTypeName === this.currentSubType);
            if (subType) {
              this.possibleSubTypes = [subType];
            }
          } else {
            this.possibleSubTypes = type.expenseSubTypes;
          }
        }
      } else {
        _.forEach(types, t => {
          this.possibleSubTypes = _.concat(this.possibleSubTypes, t.expenseSubTypes);
        });
      }

      const plans: PayFromPlanOption[] = [];

      _.forEach(this.possibleSubTypes, subtype => {
        if (subtype.planEligibility.some(x => x.isHsa) || this.currentDate.isSameOrAfter(subtype.eligibleStartDate)) {
          _.forEach(subtype.planEligibility, e => {
            let planEligibleStartDate = null;
            let account = null;
            if (e.isHsa) {
              // This date is being used for the warning message for Date of expense. Not for determining eligibility.
              planEligibleStartDate = moment(e.eligibleStartDate);
              account = this.hsabAccountsStore.findHsaAccount();
            } else {
              const planStartDate = moment(e.planStartDate);
              planEligibleStartDate = moment(e.eligibleStartDate);
              account = this.hsabAccountsStore.findNotionalAccount(e.planName, planStartDate);
            }

            const hasFinalFilingDate = account && account.finalFilingDate;
            const hasEnrollmentStartDate = this.featureFlagStore.flags.bend33049PopulateEnrollmentStartDate && account && account.enrollmentStartDate;
            const isBeforeFinalFilingDate = !hasFinalFilingDate || dateHelper.todayCST().isSameOrBefore(moment(account.finalFilingDate));
            const isNotional = !e.isHsa;
            const isAfterEligibleStartDate = e.eligibleEndDate === null ||
                (hasEnrollmentStartDate ?
                    this.currentDate.isSameOrAfter(moment(account.enrollmentStartDate)) :
                    this.currentDate.isSameOrAfter(planEligibleStartDate));

            // If notional, end date has to be after today or null, can't be past final filing date
            // If it's an HSA use the date HSAs started existing for determining eligibility.
            if ((isNotional && isAfterEligibleStartDate && isBeforeFinalFilingDate)
                || (e.isHsa && this.currentDate.isSameOrAfter(FirstDayOfExistenceForHsaAccounts))) {

              const isHsaOrHasNoEndDate = e.isHsa || isNullEmptyOrUndefined(e.eligibleEndDate);
              const hasEndingDate = !isNullEmptyOrUndefined(e.eligibleEndDate);
              if (isHsaOrHasNoEndDate || (!e.isHsa && hasEndingDate && this.currentDate.isSameOrBefore(e.eligibleEndDate))) {
                plans.push({
                  planName: e.planName,
                  planDisplayName: e.planDisplayName,
                  isHsa: e.isHsa,
                  reimbursementMethod: e.reimbursementMethod,
                  planStart: planEligibleStartDate,
                  account,
                });
              }
            }
          });
        }
      });

      this.possiblePlans = _.uniqBy(plans, x => x.planDisplayName);
      this.hsabExpenseFormStore.currentPayFromOptions = this.possiblePlans;

      if (!_.some(this.possiblePlans, x => x.isHsa) && this.forceHsaPlans) {
        this.setForceHsaPlans(false);
      }

      if (!_.isEqual(previousPossiblePlans, this.possiblePlans)) {
        this.setDefaultAllowHsa();
        this.$emit("change", this.possiblePlans);
      }

    }
  }

  onAllowHsaPlansChanged (newVal) {
    this.setAllowHsaPlans(newVal);
  }

  onForceHsaPlansChanged (newVal) {
    this.setForceHsaPlans(newVal);
  }

  setAllowHsaPlans (newVal) {
    this.allowHsaPlans = newVal;
    this.hsabExpenseFormStore.excludeHsaPlans = !this.allowHsaPlans;

    this.disableForceHsaPlansToggle = newVal;
  }

  setForceHsaPlans (newVal) {
    this.forceHsaPlans = newVal;
    this.hsabExpenseFormStore.forceHsaPlans = newVal;
    this.hsabExpenseFormStore.excludeHsaPlans = !newVal;
    this.disableAllowHsaPlansToggle = newVal;
  }

  setDefaultAllowHsa () {
    if (!this.hsabAccountsStore.userHasActiveNotionalAccounts) {
      // if user is hsa only, no point in checking pay from
      this.setAllowHsaPlans(true);
      return;
    } else if (!this.hsabAccountsStore.userHasActiveHsaAccounts) {
      // if user is notional only, no point in checking pay from
      this.setAllowHsaPlans(false);
      return;
    }

    if (this.hasNotionalAsOption && !this.hasHsaAsOption) {
      this.setAllowHsaPlans(false);
    } else if (this.hasHsaAsOption && !this.hasNotionalAsOption) {
      this.setAllowHsaPlans(true);
    } else if (this.hasHsaAsOption && this.hasNotionalAsOption && !this.hsabExpenseFormStore.forceHsaPlans && this.hsabExpenseFormStore.excludeHsaPlans) {
      this.setAllowHsaPlans(false);
    }
  }

}
